/**
 * make localstorage corresponding to object
 *
 * undefined will be stringified to null
 */
import * as Sentry from '@sentry/browser';

const createProxy = <T extends Record<string | symbol, any>>(
  obj: T,
  setCall: (...arg: any) => any,
  proxyCache = new Map(),
): T =>
  new Proxy(obj, {
    get(target, prop) {
      const originValue = target[prop];
      // only for JSON.strigify
      // https://dev.to/jankapunkt/how-stringify-proxy-to-json-10oe
      if (prop === 'toJSON') {
        return () => target;
      }
      const targetPath = proxyCache.get(target)?.[0] || [];

      if (
        ['[object Object]', '[object Array]'].indexOf(
          Object.prototype.toString.call(originValue),
        ) !== -1
      ) {
        if (!proxyCache.has(originValue)) {
          targetPath.push(prop);
          proxyCache.set(originValue, [targetPath, createProxy(originValue, setCall, proxyCache)]);
        }

        const [, proxyValue] = proxyCache.get(originValue);

        return proxyValue;
      }

      return originValue;
    },
    set(target, prop: keyof T, value) {
      target[prop] = value;
      const targetPath = proxyCache.get(target)?.[0] || [];
      setCall(targetPath.concat(prop), value);

      return true;
    },

    deleteProperty(target, prop: keyof T) {
      if (Reflect.has(target, prop)) {
        const targetPath = proxyCache.get(target)?.[0] || [];
        setCall(targetPath.concat(prop));
        Reflect.deleteProperty(target, prop);
      }

      return true;
    },
  });

const storageDict: Record<string, any> = {
  browser_uid: null,
  current_address: null,
  connected_dict: null,
  private_mode: false,
  theme: null,
  // @depreacted
  open_popular_message: false,
  hide_hi_free_modal: false,
  recentSearch: [],
  // @depreacted
  user_info: null,
  ref_code_1: {},
  translate_target: null,
  locale: 'EN',
  auto_translate_UGC: false,
  post_filter: {},
};

Object.keys(storageDict).forEach(key => {
  const value = localStorage.getItem(key);
  try {
    storageDict[key] =
      // https://developer.mozilla.org/en-US/docs/Web/API/Storage/getItem#return_value
      // key doesn't exist, return null as localStorage.getItem
      null === value ? value : 'undefined' === value ? undefined : JSON.parse(value!);
  } catch {
    storageDict[key] = value;
  }
});

export const storage = createProxy(storageDict, ([rootPath], value) => {
  try {
    localStorage.setItem(rootPath, JSON.stringify(value));
  } catch (error) {
    console.warn('[localstorage set error] ', error);
    Sentry.captureException(error);
  }
});

(window as any).storage = storage;

export default storage;
